/**
 * Appointment mutation request builders (#2237).
 *
 * COMPATIBILITY ROUTES — the appointment mutations still go through the
 * tenant-wide `/{tenantId}/appointments*` contract, which the generated
 * schema marks `@deprecated`. The canonical patient-scoped contract
 * (`/{tenantId}/patients/{patientId}/appointments*`, already used by every
 * READ via `patient-appointment-request.ts`) exposes GET only today, so a
 * full migration is gated on the backend adding patient-scoped mutations
 * and a client regeneration. Until then this module is the single seat of
 * that dependency:
 *
 * - every mutation path and its parameters are built (and contract-tested)
 *   here, so the eventual migration is a three-builder change;
 * - ownership is asserted the only way this contract allows: the patient is
 *   always attached as a confirmed participant on create and update. Note what
 *   that costs: `participants` is a delete-then-insert replacement server-side,
 *   so an update that does not name an attendee removes them. The API tolerates
 *   omitting the list — `updateAppointment` applies it only `if
 *   (data.participants)` and authorizes on `appointments.owner_id` — but the
 *   PATCH route validates against the full insert schema, where the field is
 *   required, so the generated type will not let a caller omit it. Preserving a
 *   non-patient attendee across an unrelated edit needs that schema relaxed
 *   first (see PR #1814);
 * - the generated PATCH declaration omits `{id}` from its path-parameter
 *   shape even though the URL template contains it (a tracked OpenAPI spec
 *   bug). The id is threaded at runtime through a WIDENED param type — no
 *   `as` casts — and the contract spec pins it so a regenerated schema that
 *   fixes the shape surfaces here first.
 */

export const APPOINTMENTS_CREATE_PATH = "/{tenantId}/appointments" as const;
export const APPOINTMENTS_ITEM_PATH = "/{tenantId}/appointments/{id}" as const;

export interface AppointmentMutationBody {
  cycleId?: string | null;
  title: string;
  description?: string | null;
  consultationType?: string[] | null;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  timeZone?: string;
  meetingRoom?: string | null;
  isOnline?: boolean | null;
  meetingLink?: string | null;
}

export type AppointmentParticipant = { patientId: string; status: "yes" };

/**
 * The patient must always ride along as a confirmed participant — it is the
 * only ownership signal the tenant-wide contract carries.
 *
 * Others may ride with her. A sperm collection is the partner's appointment as
 * much as hers: he is the one who has to be somewhere at that hour, and until
 * this carried him the event existed only in her calendar. She stays first in the
 * list, because she is the owner and this contract has no other way to say so,
 * and a duplicate id is dropped rather than sent twice — passing her own id as an
 * extra participant must not turn her into two attendees.
 */
export function buildAppointmentParticipants(
  patientId: string | null | undefined,
  additionalPatientIds: readonly (string | null | undefined)[] = [],
): AppointmentParticipant[] {
  const seen = new Set<string>();
  const participants: AppointmentParticipant[] = [];
  for (const id of [patientId, ...additionalPatientIds]) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    participants.push({ patientId: id, status: "yes" });
  }
  return participants;
}

export function buildCreateAppointmentRequest(args: {
  tenantId: string;
  patientId: string;
  body: AppointmentMutationBody;
  /** Attendees besides the patient — see `buildAppointmentParticipants`. */
  additionalParticipantIds?: readonly (string | null | undefined)[];
}) {
  return {
    path: APPOINTMENTS_CREATE_PATH,
    options: {
      params: { path: { tenantId: args.tenantId } },
      body: {
        ...args.body,
        participants: buildAppointmentParticipants(
          args.patientId,
          args.additionalParticipantIds,
        ),
      },
    },
  } as const;
}

export function buildUpdateAppointmentRequest(args: {
  tenantId: string;
  patientId: string | null | undefined;
  appointmentId: string;
  body: AppointmentMutationBody;
  /** Attendees besides the patient — see `buildAppointmentParticipants`. */
  additionalParticipantIds?: readonly (string | null | undefined)[];
}) {
  // The generated PATCH params omit `id` (spec bug) — thread it at runtime
  // through a type wider than the generated shape, rather than a cast, per the
  // OpenAPI package rules. Still true: the generated `patch` declares only
  // `tenantId`. `id` is non-optional here because it is always supplied; that is
  // what makes this wider than the contract, not looser than the caller.
  const path: { tenantId: string; id: string } = {
    tenantId: args.tenantId,
    id: args.appointmentId,
  };
  return {
    path: APPOINTMENTS_ITEM_PATH,
    options: {
      params: { path },
      body: {
        ...args.body,
        // Always sent — the generated PATCH declares `participants` as required.
        // Which is why the caller has to hand over whoever is already on the
        // appointment: the server replaces the whole list, so anyone missing here
        // is deleted. See `appointmentParticipantPatientIds`.
        participants: buildAppointmentParticipants(
          args.patientId,
          args.additionalParticipantIds,
        ),
      },
    },
  } as const;
}

export function buildDeleteAppointmentRequest(args: {
  tenantId: string;
  appointmentId: string;
}) {
  return {
    path: APPOINTMENTS_ITEM_PATH,
    options: {
      params: { path: { tenantId: args.tenantId, id: args.appointmentId } },
    },
  } as const;
}
